import { useCallback, useEffect, useMemo, useState } from 'react';
import { SectionCard } from '../../../components/card';
import { Stepper } from '../../../components/stepper';
import ApplicationCooLayout from '../../../layout/application-coo-layout';
import { v4 as uuidv4 } from 'uuid';
import ReviewingPage from './reviewing-page';
import ApplicationUpdateForm from './application-update-form';
import { TextSubheader } from '../../../components/text/text-subheader';
import { P2Button, P2Modal, P2Alert, P2ButtonGroup, P2TextDescription } from 'react-bootstrap-common-script';
import { FileItem } from '../../../models/polymer/Document.model';
import {
  StageDDocumentsInfo,
  StageDMessageInfo,
  StageDMessageType,
  StageDReviewApplication,
  StageDSolicitorApplication,
  StageDSolicitorApplicationForm,
  StageDSolicitorUpdate,
} from '../../../models/classes/stage-d/Review-Application.class';
import { IErrors, useForm } from '../../../hooks/use-form';
import { useGetStageD1ObjQuery } from '../../../redux/services/getStageD';
import { useAppSelector } from '../../../redux/hook';
import { PolymerAxios } from '../../../utils/axios';
import { API_ApplicationReply, API_SolicitorUpdate, API_ValuationFeePending } from '../../../utils/api';
import { isPayableValuationFeePending, StageDValuationFeeSection, type ValuationFeePendingDto } from '../valuation-fee/stage-d-valuation-fee-section';
import { useInitialWindowScrollTo } from '../../../hooks/use-initial-window-scroll-to';
import { LottieLoader } from '../../../components/loader';
import { validateStageDSolicitor } from '../../../utils/validators/stage-d/solicitor-validator';
import { cloneDeep, uniq } from 'lodash';
import moment from 'moment';
import { Helpers } from '../../../utils/helper';
import ApplicationUpdateEndorseForm from './application-update-endorse-form';
import { Separator } from '../../../components/separator';
import { currentOwnerIncludedEndorseManuallySentDraft } from '../../../utils/validators/stage-d/endorse-validator';
import { useNavigate } from 'react-router-dom';
import useStageDStepper from '../../../hooks/use-stage-d-stepper';
import { useGetApplicationUuid } from '../../../hooks/useGetApplicationUuid';

let activeStep = 1;
interface FieldValidationInterface {
  isValid: boolean;
  isPartial: boolean;
}
function StageDApplicationReview() {
  useInitialWindowScrollTo();
  const navigate = useNavigate();

  // Auth Data
  const loginUser = useAppSelector((state) => state.auth.AuthenticationSlice.loginUser);
  const singpassToken = useAppSelector((state) => state.auth.AuthenticationSlice.singpassToken);

  // Application Data
  const applicationUuid = useGetApplicationUuid();
  const startApplicationFormData = useAppSelector((state) => state.app.ApplicationStartSlice.formData);
  const applicationStep1 = useAppSelector((state) => state.app.ApplicationStep1Slice.formData);

  const { stepperItems } = useStageDStepper(loginUser.uinfin, applicationUuid || '');

  const [filesItemsStorage, setFileItemsStorage] = useState<FileItem[]>([]);
  const [pendingItems, setPendingItems] = useState<string[]>([]);

  const [isReviewing, setIsReviewing] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [valuationFeePending, setValuationFeePending] = useState<ValuationFeePendingDto | null>(null);
  const [valuationFeeChecked, setValuationFeeChecked] = useState(false);

  function backNavigate() {
    navigate(`/application-coo?application_id=${applicationUuid}`);
  }

  const {
    values: applicationReviewValues,
    setFormValue: applicationReviewSetFormValue,
  }: {
    values: StageDReviewApplication;
    setFormValue: (update: any) => void;
  } = useForm(
    new StageDReviewApplication(),
    () => {},
    () => {}
  );

  // Fetch D1 stage data from the API
  // The query is skipped if applicationUuid is not available.
  const { data, isLoading, isUninitialized, refetch } = useGetStageD1ObjQuery(
    {
      application_uuid: applicationUuid ?? '',
      uinfin: loginUser?.uinfin ?? '',
      stage: 'D1',
      singpass_token: singpassToken ?? '',
    },
    { skip: !applicationUuid }
  );

  const refreshValuationFeePending = useCallback(async (): Promise<void> => {
    if (!applicationUuid) {
      setValuationFeePending(null);
      setValuationFeeChecked(true);
      return;
    }
    setValuationFeeChecked(false);
    try {
      const res = await PolymerAxios(loginUser?.uinfin ?? '', singpassToken).get<ValuationFeePendingDto>(
        `${API_ValuationFeePending}?application_uuid=${encodeURIComponent(applicationUuid)}`
      );
      setValuationFeePending(res.data);
    } catch {
      setValuationFeePending(null);
    } finally {
      setValuationFeeChecked(true);
    }
  }, [applicationUuid, loginUser?.uinfin, singpassToken]);

  useEffect(() => {
    void refreshValuationFeePending();
  }, [refreshValuationFeePending]);

  const hasPayableValuationFee = useMemo(() => isPayableValuationFeePending(valuationFeePending), [valuationFeePending]);
  const showValuationFeeSectionOnly = valuationFeeChecked && hasPayableValuationFee;

  // Find Logged in user's Spouse
  const loginUserSpouse = applicationStep1?.household?.incoming_owners?.find((owner) => owner.myinfo.uinfin === loginUser.uinfin)?.spouse ?? '';

  /**
   * Filter the endorse_forms data to find forms that have a status of
   * "manually_sent" and "draft," where the logged-in user is an owner and the
   * form has not been signed by all owners.
   */
  const currentOwnerIncludedEndorseManuallySent = useMemo(() => {
    if (startApplicationFormData?.polymer_stage === 'E' || startApplicationFormData?.polymer_stage === 'E2') {
      return [];
    }
    return currentOwnerIncludedEndorseManuallySentDraft(data?.endorse_forms, loginUser.uinfin, loginUserSpouse);
  }, [startApplicationFormData?.polymer_stage, data?.endorse_forms, loginUser.uinfin, loginUserSpouse]);

  /**
   * Extract the number of pending forms from by logged-in user
   * "currentOwnerIncludedEndorseManuallySent"
   */
  const numPendingCurrentOwnerEndorseManuallySent = currentOwnerIncludedEndorseManuallySent.filter((form) =>
    form.owners.find((owner) => owner.uinfin === loginUser.uinfin && owner.endorsement_date === undefined)
  ).length;

  const messageInfoForCurrent = data?.solicitor_message_info?.find(
    (item) => item.owner_type === 'CURRENT_OWNER' || item.owner_type === 'OUTGOING_OWNER'
  );
  const additionalFieldsForCurrent = useMemo(() => {
    return {
      ownerType: messageInfoForCurrent ? messageInfoForCurrent?.owner_type : '',
      solicitorType: messageInfoForCurrent?.solicitor_request_type,
    };
  }, [messageInfoForCurrent?.uuid]);
  const {
    values: currentSolicitorValues,
    formValid: initialCurrentSolicitorFormValid,
    errors: currentSolicitorErrors,
    setFormValue: setCurrentSolicitorFormValue,
  }: {
    handleChange: any;
    values: StageDSolicitorApplicationForm;
    errors: IErrors;
    formValid: boolean;
    setFormValue: (update: StageDSolicitorApplicationForm) => void;
  } = useForm(new StageDSolicitorApplicationForm(), () => {}, validateStageDSolicitor, additionalFieldsForCurrent);
  const currentSolicitorFormValid = initialCurrentSolicitorFormValid || !messageInfoForCurrent;

  const messageInfoForProposed = data?.solicitor_message_info?.find((item) => item.owner_type === 'PROPOSED_OWNER');
  const additionalFieldsForProposed = useMemo(() => {
    return {
      ownerType: messageInfoForProposed ? 'PROPOSED_OWNER' : '',
      solicitorType: messageInfoForProposed?.solicitor_request_type,
    };
  }, [messageInfoForProposed?.uuid]);
  const {
    values: proposedSolicitorValues,
    formValid: initialProposedSolicitorFormValid,
    errors: proposedSolicitorErrors,
    setFormValue: setProposedSolicitorFormValue,
  }: {
    handleChange: any;
    values: StageDSolicitorApplicationForm;
    errors: IErrors;
    formValid: boolean;
    setFormValue: (update: StageDSolicitorApplicationForm) => void;
  } = useForm(new StageDSolicitorApplicationForm(), () => {}, validateStageDSolicitor, additionalFieldsForProposed);
  const proposedSolicitorFormValid = initialProposedSolicitorFormValid || !messageInfoForProposed;

  const setCurrentSolicitorFormValueHandler = useCallback(
    (values: StageDSolicitorApplication[]) => {
      const clonedValues = cloneDeep(values);
      setCurrentSolicitorFormValue({
        solicitors: clonedValues,
      });
    },
    [setCurrentSolicitorFormValue]
  );

  const setProposedSolicitorFormValueHandler = useCallback(
    (values: StageDSolicitorApplication[]) => {
      const clonedValues = cloneDeep(values);
      setProposedSolicitorFormValue({
        solicitors: clonedValues,
      });
    },
    [setProposedSolicitorFormValue]
  );

  const messageType = useMemo((): StageDMessageType => {
    //The logic here is that HDB cannot act form will only show once when type for each message should be "SOLICITOR". Else will be "GENERIC"
    // return new StageDReviewApplication(data).message_info.some((item)=> item.type === "SOLICITOR") ? "SOLICITOR" : "GENERIC";
    return data?.message_type ?? 'GENERIC';
  }, [data]);

  const solicitorMessageInfo = useMemo((): StageDMessageInfo[] | undefined => {
    return new StageDReviewApplication(data)?.solicitor_message_info?.filter((e) => e.type === 'SOLICITOR');
  }, [data]);

  const submitApplicationUpdate = async () => {
    const submittingData = processSubmitApiData();
    const submittingItems: string[] = [];
    const messagesCount = applicationReviewValues.message_info.filter((item) => item?.reply && item?.reply.length > 0).length;
    const filesCount = filesItemsStorage.length;
    if (messagesCount > 0) {
      submittingItems.push('messages');
    }

    if (filesCount > 0) {
      submittingItems.push('documents');
    }

    const updatedPendingItems = [...pendingItems].filter((item) => !submittingItems.includes(item));

    if (messageType === 'SOLICITOR' && currentSolicitorFormValid && proposedSolicitorFormValid) {
      //if has HDB cannot act form, then run this.
      submitHDBCannotActForm();
    }
    try {
      await PolymerAxios(loginUser?.uinfin ?? '', singpassToken).post(API_ApplicationReply, submittingData);
      setPendingItems(updatedPendingItems);
      setFileItemsStorage([]);
      setIsSubmitted(true);
      if (updatedPendingItems.length === 0 && currentOwnerIncludedEndorseManuallySent.length === 0) {
        setIsReviewing(true);
      } else {
        refetch();
      }
    } catch (error) {
      console.log('Error submitting application update', error);
    }
  };

  useEffect(() => {
    applicationReviewSetFormValue(data);
  }, [data, applicationUuid, applicationReviewSetFormValue]);
  const closeModal = useCallback(() => {
    setIsModalOpen(false);
  }, [isModalOpen]);
  const handleModalTrigger = useCallback(() => {
    setIsModalOpen(true);
  }, [isModalOpen]);
  const handlePrimaryButton = useCallback(() => {
    closeModal();
  }, [isModalOpen]);
  const handleSecondaryButton = useCallback(async () => {
    // to navigate => when get destination
    await submitApplicationUpdate();
    closeModal();
  }, [isModalOpen, submitApplicationUpdate]);

  const processSubmitApiData = useCallback(() => {
    const submittingMessages = [
      ...applicationReviewValues.message_info
        .filter((item) => item.type !== 'SOLICITOR' && item.reply && item.reply.length > 0)
        .map((item) => {
          return {
            due_date: item.due_date,
            received_date: item.received_date,
            subject: item.subject,
            message: item.message,
            reply: item.reply,
            group_uuid: item.group_uuid, //Groupd uuid
            parent_uuid: item.uuid, //existing uuid will tie to parent_uuid
            uuid: uuidv4(), //Generate new uuid for to biome side (can be any unique value here)
            status: item.status,
          };
        }),
    ];

    const clonedFolders = JSON.parse(JSON.stringify([...applicationReviewValues.folders]));
    for (const folder of clonedFolders) {
      for (const comment of folder.comments) {
        const files = filesItemsStorage.filter((item) => item.label === folder.name && item.file_id !== '');
        comment.documents = files.map(
          (item) =>
            new StageDDocumentsInfo({
              name: item.file_content.file_name ?? '',
              submitted_date: item.submitted_date ?? '',
              size: item?.file_content?.size ?? 0,
              uuid: item.file_id.toString(),
              key: item.key,
            })
        );
      }
    }

    const finalProcessedFolders = clonedFolders.filter((item: any) => item.comments.every((item: any) => item.documents.length !== 0));
    return {
      ...applicationReviewValues,
      message_info: applicationReviewValues.message_info.every((item) => item?.reply && item?.reply?.length > 0) ? submittingMessages : [],
      folders: filesItemsStorage.length > 0 ? finalProcessedFolders : [],
    };
  }, [applicationReviewValues, filesItemsStorage]);

  const submitHDBCannotActForm = useCallback(async () => {
    if (currentSolicitorValues.solicitors.length !== 0) {
      let formDataForCurrent = currentSolicitorValues.solicitors.map((solicitorItem) => {
        const currentOwnerMessageInfo = solicitorMessageInfo?.find(
          (info) => info.owner_type === 'CURRENT_OWNER' || info.owner_type === 'OUTGOING_OWNER'
        );
        const solicitor_obj = new StageDSolicitorUpdate();
        solicitor_obj.setApplicationUuid(applicationUuid ?? null);
        solicitor_obj.setParentUuid(solicitorItem.uuid);
        solicitor_obj.setGroupUUid(solicitorItem.group_uuid);
        if (currentOwnerMessageInfo?.owner_type === 'CURRENT_OWNER') {
          solicitor_obj.setCurrentSolicitor(solicitorItem, currentOwnerMessageInfo?.solicitor_request_type);
        }

        if (currentOwnerMessageInfo?.owner_type === 'OUTGOING_OWNER') {
          solicitor_obj.setOutgoingSolicitor(solicitorItem, currentOwnerMessageInfo?.solicitor_request_type);
        }
        return solicitor_obj.json();
      });

      try {
        await PolymerAxios(loginUser?.uinfin ?? '', singpassToken).post(API_SolicitorUpdate, formDataForCurrent);
      } catch (error) {
        console.log('Error submitting hdb cannot act form', error);
      }
    }

    if (proposedSolicitorValues.solicitors.length !== 0) {
      let formDataForProposed = proposedSolicitorValues.solicitors.map((solicitorItem) => {
        const proposedOwnerMessageInfo = solicitorMessageInfo?.find((info) => info.owner_type === 'PROPOSED_OWNER');
        const solicitor_obj = new StageDSolicitorUpdate();
        solicitor_obj.setApplicationUuid(applicationUuid ?? null);
        solicitor_obj.setParentUuid(solicitorItem.uuid);
        solicitor_obj.setGroupUUid(solicitorItem.group_uuid);
        solicitor_obj.setProposedSolicitor(solicitorItem, proposedOwnerMessageInfo?.solicitor_request_type);
        return solicitor_obj.json();
      });

      try {
        await PolymerAxios(loginUser?.uinfin ?? '', singpassToken).post(API_SolicitorUpdate, formDataForProposed);
      } catch (error) {
        console.log('Error submitting hdb cannot act form', error);
      }
    }
  }, [applicationUuid, currentSolicitorValues, proposedSolicitorValues, loginUser?.uinfin, singpassToken]);

  useEffect(() => {
    if (data?.folders.length !== 0 || data?.message_info?.length !== 0 || data?.solicitor_message_info?.length !== 0) {
      let temp: string[] = [];
      //push messages, documents according to the order of sections
      if (data?.solicitor_message_info && data?.solicitor_message_info?.length > 0) {
        temp = temp.concat(Array(data?.solicitor_message_info?.length).fill('messages'));
      }

      //push messages, documents according to the order of sections
      if (data?.message_info && data?.message_info?.length > 0) {
        temp = temp.concat(Array(data?.message_info?.length).fill('messages'));
      }

      if (data?.folders && data?.folders?.length > 0) {
        const nonLawyerLetterDocs = data.folders.filter((item) => item.name !== "Letter of Lawyer's Representation");
        if (messageType === 'GENERIC' || nonLawyerLetterDocs.length > 0) {
          //remove lawyer letter from pending items count if has solicitor message as it will be validated together with solicitor message
          const documentsCount = messageType === 'GENERIC' ? data.folders.length : nonLawyerLetterDocs.length;
          temp = temp.concat(Array(documentsCount).fill('documents'));
        }
        //fill with repeating documents as it is needed for pending items count display
      }

      setPendingItems(temp);
    } else if (messageType === 'GENERIC' && currentOwnerIncludedEndorseManuallySent.length === 0) {
      setIsReviewing(true);
    }
  }, [data, messageType, currentOwnerIncludedEndorseManuallySent]);

  const solicitorFolders = useMemo(() => {
    const lawyerLetterDocs = applicationReviewValues.folders.filter((item) => item.name === "Letter of Lawyer's Representation");
    if (messageType === 'GENERIC' || lawyerLetterDocs.length === 0) {
      return [];
    }
    const clonedLawyerLetterDoc = cloneDeep(lawyerLetterDocs[0]);
    for (const comment of clonedLawyerLetterDoc.comments) {
      //display hardcoded msg instead of comments from hdb
      comment.text = 'A legal document stating that the solicitor or law firm is representing for the party in a case.';
    }
    if (clonedLawyerLetterDoc.comments.length > 0) {
      clonedLawyerLetterDoc.comments = [clonedLawyerLetterDoc.comments[0]];
    }
    return [clonedLawyerLetterDoc];
  }, [messageType, applicationReviewValues]);

  const normalFolders = useMemo(() => {
    return messageType === 'GENERIC'
      ? applicationReviewValues.folders
      : applicationReviewValues.folders.filter((item) => item.name !== "Letter of Lawyer's Representation");
  }, [messageType, applicationReviewValues]);

  useEffect(() => {
    if (isSubmitted === true) {
      setTimeout(() => {
        setIsSubmitted(false);
      }, 1500);
    }
  }, [isSubmitted]);

  const isCurrentSolicitorFormPartial = useMemo(() => {
    const currentSolicitorMessage = (solicitorMessageInfo ?? []).find(
      (item) => item.owner_type === 'CURRENT_OWNER' || item.owner_type === 'OUTGOING_OWNER'
    );
    if (messageType === 'GENERIC' || !currentSolicitorMessage) {
      return false;
    }
    if (currentSolicitorValues.solicitors.length === 0) {
      return false;
    }
    const solicitor = currentSolicitorValues.solicitors[0];
    let checkingStringArray: string[] = [];
    const solicitorValuesArray = [
      solicitor.current.private_solicitor.name_of_firm,
      solicitor.current.private_solicitor.email_of_solicitor,
      solicitor.current.private_solicitor.uen,
    ];
    const conveyancingSolicitorValuesArray = [
      solicitor.current.conveyancing_private_solicitor.name_of_firm,
      solicitor.current.conveyancing_private_solicitor.email_of_solicitor,
      solicitor.current.conveyancing_private_solicitor.uen,
    ];
    if (currentSolicitorMessage.solicitor_request_type === 'BOTH') {
      checkingStringArray = solicitorValuesArray.concat(
        solicitor.current.private_solicitor.represented_same_solicitor ? solicitorValuesArray : conveyancingSolicitorValuesArray
      );
    } else if (currentSolicitorMessage.solicitor_request_type === 'MORTGAGE') {
      checkingStringArray = solicitorValuesArray;
    } else {
      checkingStringArray = conveyancingSolicitorValuesArray;
    }

    return checkingStringArray.some((item) => item && item.length > 0) && checkingStringArray.some((item) => !item);
  }, [messageType, solicitorMessageInfo, currentSolicitorValues]);

  const isProposedSolicitorFormPartial = useMemo(() => {
    const proposedSolicitorMessage = (solicitorMessageInfo ?? []).find((item) => item.owner_type === 'PROPOSED_OWNER');
    if (messageType === 'GENERIC' || !proposedSolicitorMessage) {
      return false;
    }
    if (proposedSolicitorValues.solicitors.length === 0) {
      return false;
    }
    const solicitor = proposedSolicitorValues.solicitors[0];
    let checkingStringArray: string[] = [];
    const solicitorValuesArray = [
      solicitor.proposed.private_solicitor.name_of_firm,
      solicitor.proposed.private_solicitor.email_of_solicitor,
      solicitor.proposed.private_solicitor.uen,
    ];
    const conveyancingSolicitorValuesArray = [
      solicitor.proposed.conveyancing_private_solicitor.name_of_firm,
      solicitor.proposed.conveyancing_private_solicitor.email_of_solicitor,
      solicitor.proposed.conveyancing_private_solicitor.uen,
    ];
    if (proposedSolicitorMessage.solicitor_request_type === 'BOTH') {
      checkingStringArray = solicitorValuesArray.concat(
        solicitor.proposed.private_solicitor.represented_same_solicitor ? solicitorValuesArray : conveyancingSolicitorValuesArray
      );
    } else if (proposedSolicitorMessage.solicitor_request_type === 'MORTGAGE') {
      checkingStringArray = solicitorValuesArray;
    } else {
      checkingStringArray = conveyancingSolicitorValuesArray;
    }

    return checkingStringArray.some((item) => item && item.length > 0) && checkingStringArray.some((item) => !item);
  }, [messageType, solicitorMessageInfo, proposedSolicitorValues]);

  const isSolicitorFormValid = useMemo((): FieldValidationInterface => {
    if (messageType !== 'SOLICITOR') {
      return {
        isValid: false,
        isPartial: false,
      };
    }

    const requiredFileLabels = solicitorFolders.filter((e) => e.is_request_document === true).map((item) => item.name);
    const uploadedFileLabels = filesItemsStorage.filter((item) => item.file_id !== '').map((item) => item.label);

    const solicitorFoldersValid =
      requiredFileLabels.length === 0 || (requiredFileLabels.length > 0 && requiredFileLabels.every((item) => uploadedFileLabels.includes(item)));

    const valid = currentSolicitorFormValid && proposedSolicitorFormValid && solicitorFoldersValid;
    let partialBooleans: boolean[] = [];
    if (messageInfoForCurrent) {
      partialBooleans.push(currentSolicitorFormValid);
      partialBooleans.push(solicitorFoldersValid);
    }

    if (messageInfoForProposed) {
      partialBooleans.push(proposedSolicitorFormValid);
      partialBooleans.push(solicitorFoldersValid);
    }

    partialBooleans = uniq(partialBooleans);

    return {
      isValid: valid,
      isPartial: partialBooleans.length > 1 || isCurrentSolicitorFormPartial || isProposedSolicitorFormPartial,
    };
  }, [
    messageType,
    currentSolicitorFormValid,
    proposedSolicitorFormValid,
    filesItemsStorage,
    isCurrentSolicitorFormPartial,
    isProposedSolicitorFormPartial,
    solicitorFolders,
    messageInfoForCurrent,
    messageInfoForProposed,
  ]);

  const isMessagesValid = useMemo((): FieldValidationInterface => {
    let valid =
      applicationReviewValues.message_info.length > 0 &&
      applicationReviewValues.message_info.every((item) => item.is_saved && item.reply && item.reply.length > 0);
    const messageSaveStates = uniq(applicationReviewValues.message_info.map((item) => item.is_saved ?? false));

    const isPartial =
      (applicationReviewValues.message_info.length > 0 && messageSaveStates.length !== 1) || //either includes save and non-saved state
      (applicationReviewValues.message_info.some(
        //only non-saved states and is typing
        (item) => item.reply && item.reply.length > 0
      ) &&
        messageSaveStates.includes(false));
    const partial = valid === true ? false : isPartial;
    return { isValid: valid, isPartial: partial };
  }, [applicationReviewValues]);

  const isDocumentsValid = useMemo((): FieldValidationInterface => {
    //Only filter the documents that is_request_document === true
    const requiredFileLabels = normalFolders.filter((e) => e.is_request_document === true).map((item) => item.name);
    const uploadedFileLabels = filesItemsStorage.filter((item) => item.file_id !== '').map((item) => item.label);

    const valid = requiredFileLabels.length > 0 && requiredFileLabels.every((item) => uploadedFileLabels.includes(item));
    const onlySomeDocumentsUploaded =
      requiredFileLabels.some((item) => uploadedFileLabels.includes(item)) && requiredFileLabels.some((item) => !uploadedFileLabels.includes(item));
    const partial = valid === true ? false : onlySomeDocumentsUploaded;
    return { isValid: valid, isPartial: partial };
  }, [filesItemsStorage, normalFolders]);

  const isButtonEnabled = useMemo((): boolean => {
    if (isDocumentsValid.isPartial || isMessagesValid.isPartial || isSolicitorFormValid.isPartial) {
      return false;
    }

    if (
      applicationReviewValues.message_info?.length > 0 &&
      (solicitorMessageInfo ?? []).length > 0 &&
      isMessagesValid.isValid !== isSolicitorFormValid.isValid
    ) {
      //require message and solicitor message to be submitted together
      return false;
    }

    return isDocumentsValid.isValid || isMessagesValid.isValid || isSolicitorFormValid.isValid;
  }, [isMessagesValid, isDocumentsValid, isSolicitorFormValid, applicationReviewValues, solicitorMessageInfo]);
  const ModalMessage = useMemo(() => {
    let messagesCount = 0;
    if (pendingItems.includes('messages')) {
      const reviewMessageCount = applicationReviewValues.message_info.filter((item) => item?.reply && item.reply.length > 0).length;
      const solicitorMessageCount = isSolicitorFormValid ? (solicitorMessageInfo?.length ?? 0) : 0;
      messagesCount = reviewMessageCount + solicitorMessageCount;
    }
    const filesCount = pendingItems.includes('documents') ? filesItemsStorage.length : 0;
    return (
      <div style={{ marginBottom: 24 }}>
        <p style={{ marginBottom: '0.5rem' }}>HDB will receive your submission of the following:</p>
        <ul className='polymer-reply-confirmation-modal-listing'>
          {messagesCount > 0 && (
            <li style={{ marginBottom: '1rem' }}>
              {messagesCount} replied message{messagesCount > 1 ? 's' : ''}
            </li>
          )}
          {filesCount > 0 && (
            <li>
              {filesCount} submitted document{filesCount > 1 ? 's' : ''}
            </li>
          )}
        </ul>
      </div>
    );
  }, [applicationReviewValues, filesItemsStorage, pendingItems, isSolicitorFormValid, solicitorMessageInfo]);

  const submittingItemsCount = useMemo(() => {
    const totalMessageCount =
      applicationReviewValues.message_info.filter((item) => item?.reply && item?.reply.length > 0).length +
      (isSolicitorFormValid.isValid ? (solicitorMessageInfo?.length ?? 0) : 0);

    let totalDocumentsCount = isDocumentsValid.isValid ? normalFolders.length : 0;
    if (isSolicitorFormValid.isValid) {
      totalDocumentsCount += solicitorFolders.length;
    }

    const messagesCount = pendingItems.includes('messages') && totalMessageCount > 0 ? totalMessageCount : 0;

    const filesCount = pendingItems.includes('documents') && totalDocumentsCount > 0 && filesItemsStorage.length > 0 ? totalDocumentsCount : 0;

    return messagesCount + filesCount;
  }, [
    applicationReviewValues,
    filesItemsStorage,
    pendingItems,
    isSolicitorFormValid,
    solicitorMessageInfo,
    isDocumentsValid,
    normalFolders,
    solicitorFolders,
  ]);

  const latestDueDate = useMemo(() => {
    const messageDueDates =
      data?.message_info.filter((item) => item.due_date && item.due_date.length > 0).map((item) => moment(item.due_date).format('yyyy-MM-DD')) ?? [];
    const foldersDueDates =
      data?.folders.filter((item) => item.due_date && item.due_date.length > 0).map((item) => moment(item.due_date).format('yyyy-MM-DD')) ?? [];
    const solicitorMessageDueDates =
      data?.solicitor_message_info
        ?.filter((item) => item.due_date && item.due_date.length > 0)
        .map((item) => moment(item.due_date).format('yyyy-MM-DD')) ?? [];
    const endorseformDueDates = (data?.endorse_forms ?? [])
      .filter(({ due_date }) => due_date)
      .map(({ due_date }) => moment(due_date).format('YYYY-MM-DD'));

    const dueDates = messageDueDates
      .concat(foldersDueDates)
      .concat(solicitorMessageDueDates)
      .concat(endorseformDueDates)
      .sort((a, b) => b.localeCompare(a));
    return dueDates.length > 0 ? dueDates[0] : moment().format('yyyy-MM-DD');
  }, [data]);

  return (
    <ApplicationCooLayout>
      <div className='view-application-stage-d-container'>
        <TextSubheader subheaderMain='Stage D: Process Application' subheaderSub={startApplicationFormData.selected_hdb_property?.address} />
        <Stepper activeStep={activeStep} items={stepperItems} style={{ margin: '62px auto' }} />
        {!isSubmitted && (pendingItems.length > 0 || numPendingCurrentOwnerEndorseManuallySent > 0) && !showValuationFeeSectionOnly && (
          <div className='polymer-stage-d-alert'>
            {' '}
            <P2Alert
              variant={'warning'}
              text={`${
                pendingItems.length + numPendingCurrentOwnerEndorseManuallySent
              } item(s) pending your actions. Please respond by ${Helpers.formatDate(latestDueDate)} or your application will be cancelled.`}
            />
          </div>
        )}

        {isSubmitted && !showValuationFeeSectionOnly && (
          <div className='polymer-stage-d-alert'>
            {' '}
            <P2Alert variant={'success'} text={'Your submission has been received by HDB.'} />
          </div>
        )}

        {!valuationFeeChecked && (
          <div className='d-flex justify-content-center'>
            <LottieLoader />
          </div>
        )}

        {/* When valuation fee is payable, hide the usual D1 content and render the self-contained payment module. */}
        {showValuationFeeSectionOnly && valuationFeePending && (
          <StageDValuationFeeSection
            pending={valuationFeePending}
            applicationUuid={applicationUuid ?? ''}
            uinfin={loginUser?.uinfin ?? ''}
            singpassToken={singpassToken ?? ''}
            onCancel={backNavigate}
            onRefresh={() => void refreshValuationFeePending()}
          />
        )}

        {valuationFeeChecked && !showValuationFeeSectionOnly && (
          <div className='d-flex justify-content-center'>
            <SectionCard
              required={false}
              className='flex-fill'
              activeStep={activeStep}
              titleChildren={<P2TextDescription title={'Updates on Application'} />}
              contentChildren={
                <>
                  {isLoading && <LottieLoader />}
                  {!isLoading && isReviewing && (
                    <ReviewingPage message='HDB is reviewing your application. We will let you know if there are any updates on the application.' />
                  )}
                  {/* <Separator variant="solid" style={{padding:"32px 0 24px 0"}}/> */}
                  {!isLoading && !isReviewing && (
                    <>
                      {currentOwnerIncludedEndorseManuallySent.length > 0 && (
                        <ApplicationUpdateEndorseForm
                          data={currentOwnerIncludedEndorseManuallySent}
                          loginUser={loginUser?.uinfin}
                          singpassToken={singpassToken ?? ''}
                          onRefetch={refetch}
                          isLoading={isLoading}
                          isUninitialized={isUninitialized}
                          applicationUuid={applicationUuid}
                        />
                      )}
                      {currentOwnerIncludedEndorseManuallySent.length > 0 &&
                        (pendingItems.includes('messages') || pendingItems.includes('documents')) && (
                          <Separator style={{ padding: '32px 0 24px 0' }} variant='solid' />
                        )}
                      <ApplicationUpdateForm
                        filesItemsStorage={filesItemsStorage}
                        setFileItemsStorage={setFileItemsStorage}
                        applicationReviewValues={applicationReviewValues}
                        applicationReviewSetFormValue={applicationReviewSetFormValue}
                        pendingItems={pendingItems}
                        messageType={messageType}
                        solicitorMessageInfo={solicitorMessageInfo}
                        currentSolicitorFormValue={setCurrentSolicitorFormValueHandler}
                        currentSolicitorErrors={currentSolicitorErrors}
                        currentSolicitorValues={currentSolicitorValues.solicitors}
                        proposedSolicitorFormValue={setProposedSolicitorFormValueHandler}
                        proposedSolicitorErrors={proposedSolicitorErrors}
                        proposedSolicitorValues={proposedSolicitorValues.solicitors}
                        normalFolders={normalFolders}
                        solicitorFolders={solicitorFolders}
                        stage='D1'
                      />
                    </>
                  )}
                </>
              }
            />
          </div>
        )}

        {valuationFeeChecked && !showValuationFeeSectionOnly && !isReviewing && (
          <div>
            <div
              style={{
                height: '62px',
                borderBottom: '1px dashed #0C8188',
                marginBottom: '24px',
              }}></div>
            <div className='polymer-button-group-wrapper' style={{ display: 'flex', justifyContent: 'center', gap: '24px' }}>
              <P2ButtonGroup>
                <P2Button variant={'outline-primary'} onClick={backNavigate}>
                  Cancel
                </P2Button>
                {isButtonEnabled ? (
                  <P2Button onClick={handleModalTrigger} variant='primary-1'>
                    Submit all
                  </P2Button>
                ) : null}
              </P2ButtonGroup>
            </div>
          </div>
        )}
      </div>
      <P2Modal
        isOpen={isModalOpen}
        onCloseModal={closeModal}
        variant='default'
        title={
          <p style={{ marginBottom: 16, fontWeight: 700, fontSize: '20px' }}>{`You are submitting: ${submittingItemsCount} item${
            submittingItemsCount > 1 ? 's' : ''
          }`}</p>
        } // in future to change to .length
        primaryButtonText='No, continue editing'
        secondaryButtonText='Yes, submit to HDB'
        primaryButtonFunction={handlePrimaryButton}
        secondaryButtonFunction={handleSecondaryButton}
        children={ModalMessage}
      />
    </ApplicationCooLayout>
  );
}

export default StageDApplicationReview;
